import { useQueryClient, useMutation } from '@tanstack/react-query';
import type { SuccessResponseBoolean } from '@elation/api-client/src/user-experiences';
import {
  dismissUserExperience,
  getGetUserExperienceQueryKey,
  useGetUserExperience as useGetUserExperienceApiClient,
} from '@elation/api-client/src/user-experiences';

import type {
  AppQueryConfig,
  UseDismissFirstTimeUserExperienceResult,
  UseDismissFirstTimeUserExperienceVariables,
  UseGetFirstTimeUserExperienceStatusOptions,
} from 'QueryTypes';
import type { UserExperiencesEntitiesState } from 'StoreTypes';

import UserExperience from 'constants/UserExperience';

const urls = {
  userExperience(userId: number, experience: UserExperience): string {
    return `/user-experiences/${userId}/experience/${experience}/`;
  },
  userExperiences(userId: number): string {
    return `/user-experiences/${userId}/`;
  },
};

/**
 * Legacy redux-query config for fetching a single user-experience flag.
 * It tracks whether the user has already dismissed the FTUX (`true`) or not (`false`).
 * @deprecated - Use useGetFirstTimeUserExperienceStatus instead
 */
export const getUserExperienceQuery = (
  userId: number,
  experience: UserExperience,
): AppQueryConfig => ({
  url: urls.userExperience(userId, experience),
  transform: (responseJson: {
    data: boolean;
  }): { userExperiences: { [experienceName: string]: boolean } } => ({
    userExperiences: { [experience]: responseJson.data },
  }),
  update: {
    userExperiences: (prevUserExperiences, newUserExperience): UserExperiencesEntitiesState => ({
      ...prevUserExperiences,
      byId: {
        ...prevUserExperiences?.byId,
        ...newUserExperience,
      },
    }),
  },
});

/**
 * React-query wrapper that returns whether the specified FTUE has already been seen
 * (i.e. `true` means dismissed/seen, `false` means still outstanding). Calling this hook
 * CAN mutate server state after the flag has been read 14 times. It will be auto dismissed.
 * Otherwise, it does not mutate server state; it simply reads and caches the status.
 */
export const useGetFirstTimeUserExperienceStatus = (
  userId: number,
  experience: UserExperience,
  options?: UseGetFirstTimeUserExperienceStatusOptions,
) => {
  const queryClient = useQueryClient();

  return useGetUserExperienceApiClient(userId, experience, {
    query: {
      enabled: options?.enabled,
      onSuccess: (data) => {
        queryClient.setQueryData(getGetUserExperienceQueryKey(userId, experience), data);
      },
    },
  });
};

/**
 * React-query mutation wrapper that marks the FTUE as “seen”/dismissed. Updates backend state
 * and mirrors that change into the shared query cache so subsequent reads see the up-to-date
 * `true` value.
 */
export const useDismissFirstTimeUserExperience = (): UseDismissFirstTimeUserExperienceResult => {
  const queryClient = useQueryClient();

  return useMutation<
    SuccessResponseBoolean,
    unknown,
    UseDismissFirstTimeUserExperienceVariables,
    unknown
  >({
    mutationFn: ({ experience, userId }) => dismissUserExperience(userId, experience),
    onSuccess: (data, variables) => {
      const response = data ?? { status: 'success', data: true };

      queryClient.setQueryData<SuccessResponseBoolean>(
        getGetUserExperienceQueryKey(variables.userId, variables.experience),
        response,
      );
    },
  });
};
